import React, { useState, useEffect } from 'react';
import FormModal from '../../../components/shared/FormModal';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import EmptyState from '../../../components/shared/EmptyState';
import { toast } from '../../../components/shared/toast';
import { apiClient } from '../../../api/apiClient';
import { usePermissions } from '../../../utils/usePermissions';
import {
  GET_ROLES_URL,
  GET_ROLE_BY_ID_URL,
  CREATE_ROLE_URL,
  UPDATE_ROLE_URL,
  DELETE_ROLE_URL,
  GET_PERMISSIONS_URL
} from '../../../api/settings';

const SYSTEM_TABS = [
  'Songs', 'Albums', 'Artists', 'Distributor', 'E-Accounts',
  'Notes & Cases', 'Calendar', 'Ringtone', 'Ownership',
  'Recode Labels', 'Revenue', 'Settings'
];

// Backend rows may use 'view'/'edit' instead of 'read'/'update' - treat them as synonyms.
const normalizeAction = (action) => {
  const lower = (action || '').toLowerCase();
  if (lower === 'view') return 'read';
  if (lower === 'edit') return 'update';
  return lower;
};

// Backend tab_name spelling can differ from the UI label (e.g. 'Record Labels' vs 'Recode Labels').
const TAB_NAME_ALIASES = {
  record_labels: 'record_labels',
  record_label: 'record_labels',
  recordlabels: 'record_labels',
  recordlabel: 'record_labels',
  recode_labels: 'record_labels',
  recode_label: 'record_labels',
  recodelabels: 'record_labels',
  recodelabel: 'record_labels',
  eaccounts: 'eaccounts',
  e_accounts: 'eaccounts',
  e_account: 'eaccounts',
  eaccount: 'eaccounts',
  notes_cases: 'notes_cases',
  notes_and_cases: 'notes_cases',
  notesandcases: 'notes_cases'
};

const normalizeTabName = (name) => {
  const normalized = (name || '').toLowerCase()
    .replace(/\s*&\s*/g, '_')
    .replace(/-/g, '')
    .replace(/\s+/g, '_');
  return TAB_NAME_ALIASES[normalized] || normalized;
};

function UserRolesSubTab({ searchQuery }) {
  const { canEdit } = usePermissions();
  const canEditSettings = canEdit('settings');

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Modals
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);

  // Form Fields
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchRoles = async () => {
    try {
      setIsLoading(true);
      const res = await apiClient(GET_ROLES_URL);
      setRoles(res?.roles || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
      toast.error('Failed to load user roles');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await apiClient(GET_PERMISSIONS_URL);
      setPermissions(res?.permissions || []);
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const handleOpenAddModal = () => {
    setRoleToEdit(null);
    setRoleName('');
    setDescription('');
    setSelectedPermissionIds([]);
    setShowRoleModal(true);
  };

  const handleOpenEditModal = async (role) => {
    try {
      setIsLoading(true);
      setRoleToEdit(role);
      setRoleName(role.role_name || '');
      setDescription(role.description || '');

      const res = await apiClient(GET_ROLE_BY_ID_URL(role.id));
      setSelectedPermissionIds(res?.permissionIds || []);
      setShowRoleModal(true);
    } catch (err) {
      console.error('Error fetching role permissions:', err);
      toast.error('Failed to load role details.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePermission = (targetPerm) => {
    if (!targetPerm) return;
    const { id: permId, tab_name: tabName, action } = targetPerm;
    const normalizedAction = normalizeAction(action);

    setSelectedPermissionIds((prev) => {
      const isAlreadyChecked = prev.includes(permId);

      if (normalizedAction === 'read') {
        if (isAlreadyChecked) {
          // Unchecking Read -> Uncheck Read and all CUD permissions for this tab module
          const tabAllPermIds = permissions
            .filter(p => normalizeTabName(p.tab_name) === normalizeTabName(tabName))
            .map(p => p.id);
          return prev.filter((id) => !tabAllPermIds.includes(id));
        } else {
          // Checking Read
          return [...prev, permId];
        }
      } else {
        // Toggling Create, Update, or Delete
        const readPerm = permissions.find(p => normalizeTabName(p.tab_name) === normalizeTabName(tabName) && normalizeAction(p.action) === 'read');
        const isReadChecked = readPerm && prev.includes(readPerm.id);

        if (!isReadChecked) {
          toast.warning(`Read permission for ${tabName} must be enabled before granting ${action.toUpperCase()} permission.`);
          return prev;
        }

        if (isAlreadyChecked) {
          return prev.filter((id) => id !== permId);
        } else {
          return [...prev, permId];
        }
      }
    });
  };

  const handleToggleAllTabAction = (tabName, action, forceCheck) => {
    const tabPerms = permissions.filter(p => normalizeTabName(p.tab_name) === normalizeTabName(tabName) && (action ? normalizeAction(p.action) === normalizeAction(action) : true));
    const permIds = tabPerms.map(p => p.id);

    setSelectedPermissionIds((prev) => {
      if (forceCheck) {
        return Array.from(new Set([...prev, ...permIds]));
      } else {
        return prev.filter(id => !permIds.includes(id));
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!roleName.trim()) {
      toast.warning('Role Name is required.');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        role_name: roleName.trim(),
        description: description.trim(),
        permission_ids: selectedPermissionIds
      };

      if (roleToEdit) {
        await apiClient(UPDATE_ROLE_URL(roleToEdit.id), {
          method: 'PUT',
          body: payload
        });
        toast.success('User Role updated successfully.');
      } else {
        await apiClient(CREATE_ROLE_URL, {
          method: 'POST',
          body: payload
        });
        toast.success('User Role created successfully.');
      }

      setShowRoleModal(false);
      fetchRoles();
    } catch (err) {
      console.error('Error saving role:', err);
      toast.error(err.message || 'Failed to save user role.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;
    try {
      await apiClient(DELETE_ROLE_URL(roleToDelete.id), {
        method: 'DELETE'
      });
      toast.success('User Role deleted.');
      setRoleToDelete(null);
      fetchRoles();
    } catch (err) {
      console.error('Error deleting role:', err);
      toast.error('Failed to delete user role.');
    }
  };

  const filteredRoles = roles.filter(r => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (r.role_name && r.role_name.toLowerCase().includes(q)) ||
      (r.description && r.description.toLowerCase().includes(q))
    );
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Top Section & Add Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
            User Roles & Permissions
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
            Manage access control and tab-level CRUD permissions for system roles.
          </p>
        </div>
        {canEditSettings && (
          <button
            type="button"
            className="action-btn-primary"
            onClick={handleOpenAddModal}
            style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '600' }}
          >
            + Add New Role
          </button>
        )}
      </div>

      {/* Roles Cards Grid */}
      {isLoading ? (
        <LoadingOverlay isLoading={true} message="Loading User Roles..." />
      ) : filteredRoles.length === 0 ? (
        <EmptyState title="No User Roles Found" message="Create a role and assign tab permissions." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
          {filteredRoles.map((role) => (
            <div
              key={role.id}
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '16px'
              }}
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                    {role.role_name}
                  </h3>
                  <span style={{ backgroundColor: '#eff6ff', color: '#0b66e3', fontSize: '12px', fontWeight: '700', padding: '2px 8px', borderRadius: '6px' }}>
                    {role.user_count || 0} Users
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0, lineHeight: '1.5' }}>
                  {role.description || 'No role description provided.'}
                </p>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: role.status === 1 ? '#059669' : '#ef4444' }}>
                  ● {role.status === 1 ? 'Active (Status: 1)' : 'Inactive'}
                </span>

                <div style={{ display: 'flex', gap: '12px' }}>
                  {canEditSettings && (
                    <button
                      type="button"
                      onClick={() => handleOpenEditModal(role)}
                      style={{ background: 'none', border: 'none', color: '#0b66e3', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                      Edit Permissions
                    </button>
                  )}
                  {canEditSettings && role.role_name !== 'Super Admin' && (
                    <button
                      type="button"
                      onClick={() => setRoleToDelete(role)}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Role & Permissions Form Modal */}
      {showRoleModal && (
        <FormModal
          isOpen={showRoleModal}
          onClose={() => setShowRoleModal(false)}
          title={roleToEdit ? `Edit Role: ${roleToEdit.role_name}` : 'Create New User Role'}
          className="modal-large"
        >
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Role Name *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder="e.g. Content Manager"
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Description
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Full CRUD access for Songs and Albums"
                />
              </div>
            </div>

            {/* Permissions Matrix */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                  Tab Module Permissions (CRUD Matrix)
                </h4>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  {selectedPermissionIds.length} permissions ticked
                </span>
              </div>

              <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', color: '#475569' }}>Tab Module</th>
                      <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Read (View)</th>
                      <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Create</th>
                      <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Update</th>
                      <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SYSTEM_TABS.map((tabName) => {
                      const readPerm = permissions.find(p => normalizeTabName(p.tab_name) === normalizeTabName(tabName) && normalizeAction(p.action) === 'read');
                      const isReadChecked = readPerm ? selectedPermissionIds.includes(readPerm.id) : false;

                      return (
                        <tr key={tabName} style={{ borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                          <td style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#0f172a' }}>
                            {tabName}
                          </td>
                          {['read', 'create', 'update', 'delete'].map((act) => {
                            const perm = permissions.find(p => normalizeTabName(p.tab_name) === normalizeTabName(tabName) && normalizeAction(p.action) === act);
                            if (!perm) return <td key={act} style={{ padding: '12px 16px' }}>—</td>;

                            const isChecked = selectedPermissionIds.includes(perm.id);
                            const isDisabled = act !== 'read' && !isReadChecked;

                            return (
                              <td key={act} style={{ padding: '12px 16px', opacity: isDisabled ? 0.35 : 1 }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={isDisabled}
                                  onChange={() => handleTogglePermission(perm)}
                                  title={isDisabled ? `Enable Read permission for ${tabName} first` : undefined}
                                  style={{ width: 16, height: 16, cursor: isDisabled ? 'not-allowed' : 'pointer', accentColor: '#0b66e3' }}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
              <button
                type="button"
                className="action-btn-outlined"
                onClick={() => setShowRoleModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="action-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : roleToEdit ? 'Save Role & Permissions' : 'Create Role'}
              </button>
            </div>
          </form>
        </FormModal>
      )}

      {/* Confirmation Modal for Role Delete */}
      <ConfirmationModal
        isOpen={!!roleToDelete}
        type="danger"
        title="Delete User Role"
        message={`Are you sure you want to delete role "${roleToDelete?.role_name}"?`}
        confirmLabel="Delete Role"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setRoleToDelete(null)}
      />
    </div>
  );
}

export default UserRolesSubTab;
